<template>
  <StToolbar tone="shell">
    <!-- Bundle switcher: the active bundle + how to switch/create/open one. -->
    <StDropdown align="left">
      <template #trigger="{ open }">
        <button class="pack-trigger" :class="{ 'is-open': open }" type="button">
          <font-awesome-icon :icon="['fas', 'box-archive']" class="pack-trigger__icon" />
          <span class="pack-trigger__text">
            <span class="pack-trigger__name">{{ activeEntry?.name ?? 'No bundle' }}</span>
            <span class="pack-trigger__edition">
              {{ activeEntry ? activeEntry.battlePack : 'Choose or create one' }}
            </span>
          </span>
          <font-awesome-icon :icon="['fas', 'chevron-down']" class="pack-trigger__caret" />
        </button>
      </template>

      <template v-if="entries.length">
        <StMenuItem v-for="e in entries" :key="e.id" @click="onSwitch(e.id)">
          <span class="pack-item">
            <font-awesome-icon
              v-if="e.id === activeId"
              :icon="['fas', 'check']"
              class="pack-item__check"
            />
            <span v-else class="pack-item__check" />
            <span class="pack-item__text">
              <span class="pack-item__name">{{ e.name }}</span>
              <span class="pack-item__edition">{{ e.battlePack }}</span>
            </span>
          </span>
        </StMenuItem>
        <div class="menu-sep" />
      </template>
      <StMenuItem icon="plus" @click="openNew">New bundle…</StMenuItem>
      <StMenuItem icon="folder-open" @click="onLoad">Open from file…</StMenuItem>
    </StDropdown>

    <div class="st-toolbar-spacer" />

    <StButton
      on-shell
      variant="ghost"
      icon="file-export"
      :disabled="!activeEntry"
      @click="onExport"
    >
      Export
    </StButton>

    <StDropdown align="right">
      <template #trigger>
        <StIconButton on-shell icon="ellipsis" label="Bundle actions" :disabled="!activeEntry" />
      </template>
      <StMenuItem icon="pencil" @click="openRename">Rename bundle…</StMenuItem>
      <StMenuItem icon="copy" @click="onDuplicate">Duplicate bundle</StMenuItem>
      <StMenuItem icon="trash" danger @click="openDelete">Delete bundle</StMenuItem>
    </StDropdown>
  </StToolbar>

  <!-- New bundle -->
  <StModal :open="showNew" title="New bundle" @close="showNew = false">
    <div class="form-stack">
      <StField
        label="Battlepack"
        :hint="isGated ? 'Locked to Core.' : 'The battlepack this content is for.'"
      >
        <StTextInput v-if="isGated" model-value="Core" disabled />
        <StSelect v-else v-model="newPackType">
          <option value="" disabled>Choose a battlepack…</option>
          <optgroup label="Matched Play">
            <option v-for="bp in mpPacks" :key="bp" :value="bp">{{ bp }}</option>
          </optgroup>
          <optgroup label="Path to Glory">
            <option v-for="bp in ptgPacks" :key="bp" :value="bp">{{ bp }}</option>
          </optgroup>
          <option :value="CUSTOM_PACK">Custom…</option>
        </StSelect>
      </StField>
      <StField
        v-if="!isGated && newPackType === CUSTOM_PACK"
        label="Custom battlepack name"
        hint="A homebrew battlepack identifier, e.g. a custom season."
      >
        <StTextInput v-model="customPack" placeholder="My Homebrew Pack" />
      </StField>
      <StField label="Name" hint="Used as the export filename.">
        <StTextInput v-model="newPackName" :placeholder="newPackPlaceholder" />
      </StField>
    </div>
    <template #footer>
      <StButton variant="ghost" @click="showNew = false">Cancel</StButton>
      <StButton variant="primary" icon="plus" :disabled="!canCreate" @click="onNew">
        Create
      </StButton>
    </template>
  </StModal>

  <!-- Rename bundle -->
  <StModal :open="showRename" title="Rename bundle" @close="showRename = false">
    <StField label="Name" hint="Used as the export filename.">
      <StTextInput v-model="renameName" @keyup.enter="onRename" />
    </StField>
    <template #footer>
      <StButton variant="ghost" @click="showRename = false">Cancel</StButton>
      <StButton variant="primary" :disabled="!renameName.trim()" @click="onRename">Save</StButton>
    </template>
  </StModal>

  <!-- Delete bundle -->
  <StModal :open="showDelete" title="Delete bundle" size="sm" @close="showDelete = false">
    <p>
      Delete <strong>{{ activeEntry?.name }}</strong> and all its contents? This can’t be undone.
    </p>
    <template #footer>
      <StButton variant="ghost" @click="showDelete = false">Cancel</StButton>
      <StButton variant="danger" icon="trash" @click="onDelete">Delete</StButton>
    </template>
  </StModal>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue';
import { useLibrary } from '../state/library.js';
import { useWorkspace } from '../state/workspace.js';
import { isGated } from '../env.js';
import { saveFile } from '../utils/fileAccess.js';
import { useBundleActions } from '../composables/useBundleActions.js';
import {
  StToolbar,
  StButton,
  StIconButton,
  StSelect,
  StTextInput,
  StField,
  StModal,
  StDropdown,
  StMenuItem,
} from './ui/index.js';

const lib = useLibrary();
const ws = useWorkspace();

const entries = computed(() => lib.entries.value);
const activeEntry = computed(() => lib.activeEntry.value);
const activeId = computed(() => lib.activeId.value);

// New-bundle modal + open-from-file flows are shared with the no-bundle gate.
const {
  showNew,
  newPackType,
  newPackName,
  customPack,
  newPackPlaceholder,
  canCreate,
  CUSTOM_PACK,
  mpPacks,
  ptgPacks,
  openNew,
  onNew,
  onLoad,
} = useBundleActions();

// ─── Modal state ───────────────────────────────────────────────────────────
const showRename = ref(false);
const showDelete = ref(false);
const renameName = ref('');

function onSwitch(id: string) {
  if (id) lib.setActive(id);
}

// ─── Rename ────────────────────────────────────────────────────────────────
function openRename() {
  renameName.value = activeEntry.value?.name ?? '';
  showRename.value = true;
}

function onRename() {
  const id = activeId.value;
  const name = renameName.value.trim();
  if (id && name) lib.renamePack(id, name);
  showRename.value = false;
}

// ─── Delete ────────────────────────────────────────────────────────────────
function openDelete() {
  showDelete.value = true;
}

function onDelete() {
  const entry = activeEntry.value;
  if (entry) lib.deletePack(entry.id);
  showDelete.value = false;
}

// ─── Duplicate ───────────────────────────────────────────────────────────────
function onDuplicate() {
  const id = activeId.value;
  if (id) lib.duplicatePack(id);
}

// ─── Export ────────────────────────────────────────────────────────────────
async function onExport() {
  const entry = activeEntry.value;
  if (!entry) return;
  lib.syncActiveFromWorkspace(); // flush latest edits into the entry
  // Key the remembered directory by the bundle's stable id (survives renames).
  await saveFile(`${entry.name}.json`, ws.exportBundle(), entry.id);
}
</script>

<style scoped>
/* ─── Bundle switcher trigger ─────────────────────────────────────────────── */
.pack-trigger {
  display: flex;
  align-items: center;
  gap: var(--st-space-2);
  height: var(--st-control-h-lg);
  max-width: 18rem;
  padding: 0 var(--st-space-2) 0 var(--st-space-3);
  border: 1px solid transparent;
  border-radius: var(--st-radius-md);
  background: transparent;
  color: var(--st-shell-text);
  cursor: pointer;
  transition: var(--st-transition);
}
.pack-trigger:hover,
.pack-trigger.is-open {
  background: var(--st-shell-surface);
  border-color: var(--st-shell-border);
}
.pack-trigger__icon {
  color: var(--st-accent-on-dark);
  font-size: var(--st-text-md);
}
.pack-trigger__text {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  min-width: 0;
  line-height: 1.15;
}
.pack-trigger__name {
  max-width: 13rem;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: var(--st-text-sm);
  font-weight: var(--st-weight-semibold);
}
.pack-trigger__edition {
  max-width: 13rem;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: var(--st-text-2xs);
  color: var(--st-shell-text-subtle);
}
.pack-trigger__caret {
  font-size: 0.7em;
  color: var(--st-shell-text-subtle);
}

/* ─── Switcher menu items ─────────────────────────────────────────────────── */
.pack-item {
  display: flex;
  align-items: center;
  gap: var(--st-space-2);
  min-width: 11rem;
}
.pack-item__check {
  flex-shrink: 0;
  width: 0.9rem;
  font-size: 0.8em;
  color: var(--st-accent);
}
.pack-item__text {
  display: flex;
  flex-direction: column;
  line-height: 1.2;
}
.pack-item__edition {
  font-size: var(--st-text-2xs);
  color: var(--st-text-subtle);
}
.menu-sep {
  height: 1px;
  margin: var(--st-space-1) 0;
  background: var(--st-border);
}

/* ─── Modal form layout ───────────────────────────────────────────────────── */
.form-stack {
  display: flex;
  flex-direction: column;
  gap: var(--st-space-4);
}
</style>
